import { Component, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import { lucideEllipsisVertical, lucidePen, lucidePlus, lucideTrash } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { CardItemImports } from '../../../../shared/components/card-item';
import { ConfirmDialogComponent } from '../../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { ExpansionPanelMenuAction } from '../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { IndiceRésultats } from '../../processus-indice-resultats/models/indice-résultats';
import { Phase } from '../../processus-phases/models/phase.model';
import { PhaseStore } from '../../processus-phases/store/phase.store';
import { FormIndicateursComponent } from '../form/form-indicateurs/form-indicateurs.component';
import { IndicateurPerformance } from '../models/indicateur-performance';
import { IndicateurPerformanceStore } from '../store/indicateur-performance.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-indicateur-item',
  imports: [HlmButtonImports, HlmDropdownMenuImports, HlmIconImports, CardItemImports],
  templateUrl: './indicateur-item.component.html',
  providers: [
    provideIcons({
      lucidePlus,
      lucideTrash,
      lucideEllipsisVertical,
    }),
  ],
})
export class IndicateurItemComponent {
  readonly #indicateurStore = inject(IndicateurPerformanceStore);
  readonly #phaseStore = inject(PhaseStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly phases = input.required<Phase[]>();
  readonly referentiel = input.required<Référentiel>();
  readonly peutModifier = input.required<boolean>();
  readonly indicateur = input.required<IndicateurPerformance>();
  readonly indiceResultats = input.required<IndiceRésultats>();

  readonly indicateurActions: ExpansionPanelMenuAction<IndicateurPerformance>[] = [
    {
      icon: 'lucideTrash',
      label: "Supprimer l'indicateur",
      action: () => this.#supprimerIndicateurPerformance(),
    },
  ];

  #supprimerIndicateurPerformance(): void {
    const indiceResultats = this.indiceResultats();
    const indicateur = this.indicateur();

    if (!indicateur || !indiceResultats) return;
    let dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: `Supprimer l'indicateur`,
        contenu: `Êtes-vous sûr de vouloir supprimer l'indicateur ${indicateur.nom} ?`,
        confirmBtnText: `Confirmer la suppression`,
      },
    });

    const toastrId = uuidv7();

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        const idsIndice = indicateur.idsIndicesResultats?.filter((id) => id !== indiceResultats.id);
        this.#indicateurStore.modifier({
          data: {
            indicateurs: [
              {
                id: indicateur.id,
                nom: indicateur.nom,
                idsIndicesResultats: idsIndice,
                idReferentiel: this.referentiel().id,
              },
            ],
          },
          next: () => {
            this.#phaseStore.charger(this.referentiel().id);
            toast.custom(ToastrSuccessComponent, {
              componentProps: {
                message: 'Indice délié',
                id: toastrId,
              },
              id: toastrId,
            });
          },
        });
      }
    });
  }

  ajoutIndicateurPerformance(): void {
    if (this.indiceResultats()) return;

    this.#sidepanelService.setComponentToDisplay(
      'Ajouter un indicateur de performance',
      FormIndicateursComponent,
      `Modifiez le nom et le parent de votre indicateur de performance.`,
      {
        aptitudes: this.phases().flatMap((x) => x.aptitudes),
        estCreation: true,
        referentiel: this.referentiel(),
        indiceResultats: this.indiceResultats(),
      },
    );
    this.#sidepanelService.open();
  }
}
